import { useCallback, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/shallow';

import { type PlacementSolution } from 'api/models';
import { PlacementUpdateEvent } from 'app/modules/wallforming/components/content-container/types/placementUpdateEvent';
import { useInputActionHandler } from 'app/modules/wallforming/components/input-binding/hooks/useInputActionHandler';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { mapPlacementToArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import {
    applyCurrentPoiPlacementVisibility,
    applyPrevPoiPlacementVisibility,
    getSolutionKey
} from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const getArticleInstances = (articles: Record<string, string[]> | undefined) =>
    Object.fromEntries(Object.entries(articles ?? {}).map(([articleId, instanceIds]) => [articleId, [...instanceIds]]));

export const usePlacementSolutionHandler = () => {
    const solutionItemRefs = useRef<Map<number, HTMLDivElement>>(new Map());

    const {
        solutions,
        selectedSolution,
        showPlacementPreview,
        setSelectedSolution,
        setShowPlacementPreview,
        setAppliedSolutionForPoi,
        resetHiddenInstanceIds
    } = usePlacementStore(
        useShallow((state) => ({
            solutions: state.placementSolutions,
            selectedSolution: state.selectedPlacementSolution,
            showPlacementPreview: state.showPlacementPreview,
            setSelectedSolution: state.setSelectedPlacementSolution,
            setShowPlacementPreview: state.setShowPlacementPreview,
            setAppliedSolutionForPoi: state.setAppliedCornerPlacementSolutionForPoi,
            resetHiddenInstanceIds: state.resetHiddenInstanceIds
        }))
    );

    const appliedKey = usePlacementStore((state) => {
        const selectedPoiIndex = state.poiSelection.current.index;
        return selectedPoiIndex === undefined
            ? null
            : (state.appliedCornerPlacementSolutionByPoi[selectedPoiIndex] ?? null);
    });

    const isSolutionApplied = useCallback(
        (solution: PlacementSolution) => appliedKey !== null && appliedKey === getSolutionKey(solution),
        [appliedKey]
    );

    useEffect(() => {
        const subscribedPoiSelection = usePlacementStore.subscribe(
            (state) => state.poiSelection.current.index,
            (_newPoiIndex, prevPoiIndex) => {
                setSelectedSolution(null);
                resetHiddenInstanceIds();

                requestAnimationFrame(() => {
                    applyPrevPoiPlacementVisibility(prevPoiIndex, usePlacementStore.getState());
                    applyCurrentPoiPlacementVisibility(usePlacementStore.getState());
                });

                requestAnimationFrame(() => {
                    usePlacementStore.getState().updateArticleEdgesOverlay();
                });
            }
        );

        return () => subscribedPoiSelection();
    }, [resetHiddenInstanceIds, setSelectedSolution]);

    const handleOnApplySolution = useCallback(
        (index: number | null) => {
            const solution = index === null ? undefined : solutions?.[index];
            if (!solution || isSolutionApplied(solution)) {
                return;
            }

            const state = usePlacementStore.getState();
            const selectedPoiIndex = state.poiSelection.current.index;
            const selectedPoi = selectedPoiIndex === undefined ? undefined : state.pois[selectedPoiIndex];
            const existingIds = getArticleInstances(selectedPoi?.articles);

            const updatedInstances = solution.placements.flatMap((group) =>
                group.instances.map((instance) => ({
                    articleId: group.articleGuid,
                    instanceId: instance.id,
                    instance: mapPlacementToArticleInstance(instance)
                }))
            );

            state.runHistoryTransaction('historyPlace', () => {
                state.removeArticleInstances(existingIds, true);
                state.updateArticleInstances(updatedInstances, true);
                usePlacementStore.setState({
                    placementUpdate: {
                        event: PlacementUpdateEvent.PLACE,
                        articleInstanceIds: new Set(updatedInstances.map(({ instanceId }) => instanceId))
                    }
                });

                if (selectedPoiIndex !== undefined) {
                    setAppliedSolutionForPoi(selectedPoiIndex, getSolutionKey(solution));
                }
            });

            requestAnimationFrame(() => {
                applyCurrentPoiPlacementVisibility(usePlacementStore.getState());
            });
        },
        [isSolutionApplied, setAppliedSolutionForPoi, solutions]
    );

    const handleOnReset = useCallback(() => {
        if (selectedSolution === null) {
            return;
        }

        const state = usePlacementStore.getState();
        const selectedPoiIndex = state.poiSelection.current.index;
        if (selectedPoiIndex === undefined) {
            return;
        }

        const removals = getArticleInstances(state.pois[selectedPoiIndex]?.articles);

        state.runHistoryTransaction('historyDelete', () => {
            state.removeArticleInstances(removals, true);
            state.setAppliedCornerPlacementSolutionForPoi(selectedPoiIndex, null);
        });
        setSelectedSolution(null);
    }, [selectedSolution, setSelectedSolution]);

    const handleOnDoubleClick = useCallback(
        (event: React.MouseEvent, index: number) => {
            setSelectedSolution(index);
            handleOnApplySolution(index);
            event.preventDefault();
        },
        [handleOnApplySolution, setSelectedSolution]
    );

    const handleOnPointerDown = useCallback(
        (index: number) => {
            setSelectedSolution(index);
        },
        [setSelectedSolution]
    );

    useInputActionHandler(InputActionId.PlacementApplySelectedSolution, () => handleOnApplySolution(selectedSolution));

    useEffect(() => {
        if (selectedSolution === null) {
            return;
        }

        solutionItemRefs.current.get(selectedSolution)?.scrollIntoView({ block: 'nearest' });
    }, [selectedSolution, solutions]);

    return {
        solutions,
        selectedSolution,
        showPlacementPreview,
        hasAppliedSolution: appliedKey !== null,
        isSolutionApplied,
        solutionItemRefs,
        handleOnApplySolution,
        handleOnReset,
        handleOnDoubleClick,
        handleOnPointerDown,
        setShowPlacementPreview
    };
};
